import { useCallback, useEffect, useState } from 'react';
import { getQuizRevisionConversation } from '../services/quizRevisionService';

/**
 * Hook to fetch conversationId for a single quiz question
 * @param {string} quizJobGuid - Quiz job GUID
 * @param {string} questionReferenceId - Question reference ID (same as questionGuid)
 * @returns {Object} { conversationId, isLoading, error, refetch }
 */
export function useQuizRevisionConversation(quizJobGuid, questionReferenceId) {
  const [conversationId, setConversationId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchConversation = useCallback(async () => {
    if (!quizJobGuid || !questionReferenceId) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await getQuizRevisionConversation(quizJobGuid, questionReferenceId);
      setConversationId(response.conversationId);
    } catch (err) {
      // 404 means no conversation exists - not necessarily an error
      if (err.statusCode === 404) {
        setConversationId(null);
      } else {
        console.error('[useQuizRevisionConversation] Error fetching conversation:', err);
        setError(err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [quizJobGuid, questionReferenceId]);

  useEffect(() => {
    fetchConversation();
  }, [fetchConversation]);

  return {
    conversationId,
    isLoading,
    error,
    refetch: fetchConversation,
  };
}

export default useQuizRevisionConversation;
